import axios from "axios";
import { getTokens, setTokens, clearTokens } from "./tokenStorage";

// CRA's dev server proxies relative /api requests to the Express server
// (see "proxy" in package.json), so no base URL is needed in development.
// In production this should be set to the deployed API's origin.
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "/api/v1",
});

// Bearer token attached explicitly rather than an httpOnly cookie sent
// automatically — frontend and backend are on different domains in
// production, and browsers (Safari especially) block cross-site cookies
// by default regardless of SameSite/Secure settings. A custom header
// has no such restriction.
api.interceptors.request.use((config) => {
  const { accessToken } = getTokens();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Access tokens are short-lived (15m) — a 401 on any authenticated
// request tries exactly one silent refresh-and-retry before giving up,
// so a still-valid session doesn't kick anyone out just because the
// access token expired mid-visit. `_retried` guards against ever
// looping twice.
let refreshPromise = null;

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { config, response } = error;
    if (response?.status !== 401 || config._retried || config.url === "/auth/refresh") {
      return Promise.reject(error);
    }
    config._retried = true;

    const { refreshToken } = getTokens();
    if (!refreshToken) return Promise.reject(error);

    refreshPromise ||= api
      .post("/auth/refresh", { refreshToken })
      .then((res) => setTokens({ accessToken: res.data.accessToken }))
      .catch((err) => {
        clearTokens();
        throw err;
      })
      .finally(() => {
        refreshPromise = null;
      });

    try {
      await refreshPromise;
      config.headers.Authorization = `Bearer ${getTokens().accessToken}`;
      return api(config);
    } catch {
      return Promise.reject(error);
    }
  }
);

export default api;
